"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

/**
 * Phone-only quick actions pinned to the bottom of the viewport once the hero
 * has scrolled out of view, and tucked away again when the end of the
 * homepage content (just above the site footer) comes into view. Hidden from
 * md up.
 */
export function StickyPhoneCta({
  heroId,
  endId,
  jobsHref,
}: {
  heroId: string;
  endId: string;
  jobsHref: string;
}) {
  const [heroOut, setHeroOut] = useState(false);
  const [endIn, setEndIn] = useState(false);
  const visible = heroOut && !endIn;

  useEffect(() => {
    const hero = document.getElementById(heroId);
    const end = document.getElementById(endId);
    if (!hero || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === hero) setHeroOut(!entry.isIntersecting);
        else setEndIn(entry.isIntersecting);
      }
    });
    observer.observe(hero);
    if (end) observer.observe(end);
    return () => observer.disconnect();
  }, [heroId, endId]);

  return (
    <nav
      aria-label="Quick actions"
      aria-hidden={!visible}
      inert={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 flex gap-2.5 border-t border-line bg-white px-4 pb-[max(14px,env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-8px_24px_rgba(22,35,59,0.08)] transition-transform duration-300 motion-reduce:transition-none md:hidden ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <Link
        href={jobsHref}
        data-ph-capture-attribute-home-cta="sticky-browse-jobs"
        className="flex min-h-12 flex-1 items-center justify-center whitespace-nowrap rounded-[12px] bg-teal px-3 text-[15px] font-semibold text-white no-underline shadow-[0_10px_26px_rgba(28,138,153,0.3)]"
      >
        Browse jobs
      </Link>
      <Link
        href="/for-employers"
        data-ph-capture-attribute-home-cta="sticky-hiring"
        className="flex min-h-12 flex-1 items-center justify-center whitespace-nowrap rounded-[12px] border-[1.5px] border-navy bg-white px-3 text-[15px] font-semibold text-navy no-underline"
      >
        I&rsquo;m hiring
      </Link>
    </nav>
  );
}
